import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type StatusCheckNow, type StatusIncidentsQuery } from '@utazzo/shared';

import {
  getServiceHistory,
  getStatusOverview,
  listStatusIncidents,
  runStatusChecks,
  setIncidentNote,
} from './status.api';

export const statusQK = {
  all: ['status'] as const,
  overview: ['status', 'overview'] as const,
  history: (key: string) => ['status', 'history', key] as const,
  incidents: (q: Partial<StatusIncidentsQuery>) => ['status', 'incidents', q] as const,
};

/// The page is something admins watch during an outage, so it polls:
/// the fast probes run every minute, so one refetch a minute is enough.
export const useStatusOverview = () =>
  useQuery({
    queryKey: statusQK.overview,
    queryFn: getStatusOverview,
    staleTime: 20_000,
    refetchInterval: 60_000,
  });

export const useServiceHistory = (key: string | null) =>
  useQuery({
    queryKey: statusQK.history(key ?? ''),
    queryFn: () => getServiceHistory(key as string),
    enabled: key !== null,
    staleTime: 30_000,
  });

export const useStatusIncidents = (q: Partial<StatusIncidentsQuery>) =>
  useQuery({
    queryKey: statusQK.incidents(q),
    queryFn: () => listStatusIncidents(q),
    staleTime: 20_000,
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
  });

export const useSetIncidentNote = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => setIncidentNote(id, note),
    onSuccess: () => void qc.invalidateQueries({ queryKey: statusQK.all }),
  });
};

export const useRunStatusChecks = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: StatusCheckNow) => runStatusChecks(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: statusQK.all }),
  });
};
